import { AnalyticsUpIcon } from "@hugeicons-pro/core-solid-rounded";
import { useLingui } from "@lingui/react/macro";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { formatISO, isValid, parseISO } from "date-fns";
import { useState } from "react";
import { DownloadsOverview } from "~/components/charts/downloads-overview";
import { TopCountries } from "~/components/charts/top-countries";
import { TopDevices } from "~/components/charts/top-devices";
import { TopDirectories } from "~/components/charts/top-directories";
import { TopEpisodes } from "~/components/charts/top-episodes";
import { TopPlayers } from "~/components/charts/top-players";
import { TopPodcasts } from "~/components/charts/top-podcasts";
import { ChartFilters } from "~/components/charts/ui/chart-filters";
import { AppContainer } from "~/components/layouts/app/app-container";
import { PageHead } from "~/components/ui/page-head";
import {
	buildQuickFiltersData,
	QUICK_FILTERS,
	type QuickFilter,
	useChartFilters,
} from "~/hooks/user-chart-filters";
import { exportCsv } from "~/lib/export";
import { useTRPC } from "~/trpc/client";

type StatisticsSearch = {
	q: QuickFilter;
	from?: string;
	to?: string;
};

export const Route = createFileRoute("/_app/manager/statistics")({
	component: Page,
	validateSearch: (search: Record<string, unknown>): StatisticsSearch => {
		const q = typeof search.q === "string" && QUICK_FILTERS.includes(search.q) ? search.q : "7d";

		const quickFilters = buildQuickFiltersData();
		const quickFilter = !["all", "custom"].includes(q)
			? quickFilters.find((f) => f.key === q)
			: undefined;

		return {
			q,
			from:
				typeof search.from === "string" && isValid(parseISO(search.from))
					? search.from
					: quickFilter?.value?.from
						? formatISO(quickFilter.value.from, { representation: "date" })
						: undefined,
			to:
				typeof search.to === "string" && isValid(parseISO(search.to))
					? search.to
					: quickFilter?.value?.to
						? formatISO(quickFilter.value.to, { representation: "date" })
						: undefined,
		};
	},
});

function Page() {
	const { t } = useLingui();
	const search = Route.useSearch();
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const [isExporting, setIsExporting] = useState(false);

	const { data: channels } = useQuery(trpc.channel.list.queryOptions());
	const allStartDate =
		channels?.reduce((acc, channel) => {
			if (new Date(channel.createdDate) < acc) {
				return new Date(channel.createdDate);
			}
			return acc;
		}, new Date()) ?? new Date();

	const chartFilters = useChartFilters({
		allStartDate,
		initialFilter: search.q,
		initialDate:
			search.from && search.to
				? { from: new Date(search.from), to: new Date(search.to) }
				: undefined,
	});

	async function dailyDownloads() {
		const data = await queryClient.fetchQuery(
			trpc.statistics.reportDailyDownloads.queryOptions({
				from: chartFilters.filters.from,
				to: chartFilters.filters.to,
			}),
		);

		if (!data) {
			throw Error("This report doesn't have any data to export.");
		}

		const csvContent = data.map((dataPoint) => ({
			date: dataPoint.date,
			episodeUid: dataPoint.episode?.uid || "",
			episodeTitle: dataPoint.episode?.title || "",
			channelUid: dataPoint.episode?.channelUid || "",
			channelTitle: dataPoint.episode?.channelTitle || "",
			publishDate: dataPoint.episode?.publishDate || "",
			downloads: dataPoint.downloads,
		}));

		let title = `report_daily_downloads`;
		title += `_${chartFilters.filters.from}_${chartFilters.filters.to}`;
		title += `.csv`;

		return {
			title,
			csvContent,
		};
	}
	async function totalDownloads() {
		const data = await queryClient.fetchQuery(
			trpc.statistics.reportTotalDownloads.queryOptions({}),
		);

		if (!data) {
			throw Error("This report doesn't have any data to export.");
		}

		const csvContent = data.map((dataPoint) => ({
			episodeUid: dataPoint.episode?.uid || "",
			episodeTitle: dataPoint.episode?.title || "",
			channelUid: dataPoint.episode?.channelUid || "",
			channelTitle: dataPoint.episode?.channelTitle || "",
			publishDate: dataPoint.episode?.publishDate || "",
			downloads_1_day: dataPoint.downloads["1D"],
			downloads_7_day: dataPoint.downloads["7D"],
			downloads_30_day: dataPoint.downloads["30D"],
			downloads_90_day: dataPoint.downloads["90D"],
			downloads_total: dataPoint.downloads.total,
		}));

		let title = `report_total_downloads`;
		title += `.csv`;

		return {
			title,
			csvContent,
		};
	}

	const handleExport = async (report: string) => {
		setIsExporting(true);

		try {
			let title = "";
			let csvContent = [];

			switch (report) {
				case "dailyDownloads": {
					const report = await dailyDownloads();

					title = report.title;
					csvContent = report.csvContent;
					break;
				}
				case "totalDownloads": {
					const report = await totalDownloads();

					title = report.title;
					csvContent = report.csvContent;
					break;
				}
				default:
					throw Error("Unknown report type");
			}

			try {
				exportCsv(title, csvContent);
			} catch (error) {
				alert(
					t`An error occurred while exporting the CSV: ${error instanceof Error ? error.message : String(error)}`,
				);
			}
		} catch (e) {
			console.error(e);
			alert(t`Failed to export data. Please try again later.`);
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<>
			<PageHead
				icon={AnalyticsUpIcon}
				title={t`Global Statistics`}
				subtitle={t`Track your growth with insights into your most popular podcasts, episodes, and audience trends.`}
			/>
			<AppContainer className="my-12">
				<ChartFilters hook={chartFilters} handleExport={handleExport} isExporting={isExporting} />
				<div className="grid grid-cols-12 gap-6">
					<DownloadsOverview
						className="col-span-full"
						channels={channels}
						filters={chartFilters.filters}
					/>
					<TopPodcasts
						className="col-span-full lg:col-span-6"
						channels={channels}
						filters={chartFilters.filters}
					/>
					<TopEpisodes
						className="col-span-full lg:col-span-6"
						channels={channels}
						filters={chartFilters.filters}
					/>
					<TopCountries
						className="col-span-full"
						channels={channels}
						filters={chartFilters.filters}
					/>
					<TopDevices
						className="col-span-full md:col-span-6"
						channels={channels}
						filters={chartFilters.filters}
					/>
					<TopPlayers
						className="col-span-full md:col-span-6"
						channels={channels}
						filters={chartFilters.filters}
					/>
					<TopDirectories
						className="col-span-full md:col-span-6"
						channels={channels}
						filters={chartFilters.filters}
					/>
				</div>
			</AppContainer>
		</>
	);
}
